import React, { useEffect, useState } from "react";
import { Modal } from "../../../../components/modal/Modal";
import { useAtiraFormContext } from "../../context/form-context";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { Flex } from "../../../../components/Flex";
import { Input } from "../../../../components/Input";
import { Lengths } from "../../../../model/shared/enum/Lengths.enum";
import { Button } from "../../../../components/Button";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { formActions } from "../../../../redux/forms/form.slice";
import { UpdateFormsFolderDto } from "../../../../model/form/dto/UpdateFormsFolderDto";
import { onSetTypedCRUDErrors } from "../../../../utils/onSetTypedCRUDErrors";
import styled from "styled-components";
import { Spacing } from "../../../../theme/Spacing";
import { Text } from "../../../../components/Text";
import { getFontAwesomeIcon } from "../../../../utils/getFontAwesomeIcon";
import { faAngleDown } from "@fortawesome/free-solid-svg-icons/faAngleDown";
import { AtiraIcon } from "../../../../components/AtiraIcon";
import { IconSelectModal } from "../../../../components/IconSelectModal";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { Textarea } from "../../../../components/Textarea";
import { TestIDs } from "../../../../tests/types/testIds";

const IconsButton = styled(Button)`
  width: fit-content;
  background-color: transparent;
  color: ${({ theme }) => theme.main};
  border: ${({ theme }) => `1px solid ${theme.main}`};

  &:hover {
    background-color: ${({ theme }) => theme.main};
    color: ${({ theme }) => theme.sub};
  }
`;

const CurrentIconContainer = styled(Flex)`
  align-items: center;
  gap: ${Spacing.s};
  border-inline-start: ${({ theme }) => `4px solid ${theme.main}`};
  padding-inline-start: ${Spacing.s};
`;

export const FormsFolderUpdateModal: React.FC = () => {
  const [iconsModalVisible, setIconsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;

  const dispatch = useAppDispatch();
  const {
    selectedFolder,
    formsFolderUpdateVisible,
    setFormsFolderUpdateVisible,
  } = useAtiraFormContext();
  const { t } = useTranslation();
  const { control, reset, handleSubmit, getValues, setError, setValue, watch } =
    useForm<UpdateFormsFolderDto>();

  const icon = watch("icon");

  const localOnClose = () => {
    setFormsFolderUpdateVisible(false);
    reset();
  };

  const onUpdate = async () => {
    try {
      setLoading(true);

      await dispatch(formActions.updateFolder(getValues())).unwrap();
      await dispatch(formActions.getFolders({ userId })).unwrap();

      localOnClose();

      AtiraToast.success(t("forms.folder.update.success"));
    } catch (e) {
      console.log(e);
      AtiraToast.apiError(e);
      onSetTypedCRUDErrors(e, setError);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedFolder && formsFolderUpdateVisible) {
      reset({
        formFolderId: selectedFolder._id,
        name: selectedFolder.name,
        description: selectedFolder?.description,
        icon: selectedFolder?.icon,
        userId,
      });
    }
  }, [reset, selectedFolder, userId, formsFolderUpdateVisible]);

  return (
    <Modal
      title={t("forms.folder.update.modal.title")}
      open={formsFolderUpdateVisible}
      onClose={localOnClose}
    >
      <Flex width={"100%"} flexDirection="column" gap="m">
        <Controller
          control={control}
          name="name"
          rules={{
            required: {
              value: true,
              message: t("common.fieldRequired"),
            },
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("common.name"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Input
              title={t("common.name")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              required
              maxLength={Lengths.DESCRIPTION}
              dataTestId={TestIDs.forms.createEditFolder.modal.name}
            />
          )}
        />

        <Controller
          control={control}
          name="description"
          rules={{
            maxLength: {
              value: Lengths.DESCRIPTION,
              message: t("error.length.less", {
                name: t("common.description"),
                length: Lengths.DESCRIPTION,
              }),
            },
          }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Textarea
              title={t("common.description")}
              value={value}
              onChange={onChange}
              valid={!error}
              errorMessage={error?.message}
              dataTestId={TestIDs.forms.createEditFolder.modal.description}
              maxLength={Lengths.DESCRIPTION}
            />
          )}
        />

        <Flex flexDirection="column" gap="xs" width={"100%"}>
          <Text>{t("forms.folder.create.modal.icon.title")}</Text>

          <Flex flexDirection="column" gap="s" marginTop="s">
            {icon ? (
              <CurrentIconContainer>
                <Text fontWeight={"bold"} color="main">
                  {t("common.currentIcon")}:
                </Text>

                <AtiraIcon
                  icon={getFontAwesomeIcon(icon) as IconProp}
                  size="lg"
                />
              </CurrentIconContainer>
            ) : null}

            <IconsButton
              onClick={() => setIconsModalVisible(true)}
              margin="none"
              icon={faAngleDown}
              dataTestId={TestIDs.forms.createEditFolder.modal.icon}
            >
              {t("modules.definition.basicInfo.icon.button")}
            </IconsButton>
          </Flex>
        </Flex>

        <Button
          onClick={handleSubmit(onUpdate)}
          loading={loading}
          margin="none"
          dataTestId={TestIDs.forms.createEditFolder.modal.submit}
        >
          {t("common.update")}
        </Button>
      </Flex>

      <IconSelectModal
        open={iconsModalVisible}
        onClose={() => setIconsModalVisible(false)}
        onChange={(value) => setValue("icon", value)}
        currentIcon={icon}
      />
    </Modal>
  );
};
